import { useState, useEffect, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import rrulePlugin from '@fullcalendar/rrule';
import nlLocale from '@fullcalendar/core/locales/nl';
import { useTaskDTO } from '../../../hooks/firebase/TaskDto.jsx';
import { getTaskTypeStyle } from '../../../helpers/taskTypeStyles.js';
import { agendaEventStyle } from '../../../helpers/calendar/personaAgendaColors';
import { buildRecurringEventProps } from '../../../helpers/calendar/wallClockRecurrence';
import { scheduleCutoff } from '../../../helpers/calendar/agendaSchedulePersonas';
import useAgendaSchedule from '../../../hooks/calendar/useAgendaSchedule.js';
import CalendarFilters from '../../calendar/CalendarFilters.jsx';
import useTimelineView, { TIMELINE_VIEW } from '../../../hooks/calendar/useTimelineView.js';
import { useFirestore, useFirestorePersonaAbsences } from '../../../firebase/useFirestore.js';
import { client } from '../../../hooks/Client.js';
import resolveAgendaOptions from '../../../helpers/calendar/resolveAgendaOptions.js';
import personaAbsenceClassNames from '../../../helpers/calendar/personaAbsenceClassNames';
import scheduleDisplayFor, { personaIdsWithSchedule, scheduleOffClasses, asScheduleBackground } from '../../../helpers/calendar/scheduleDisplayFor';

/**
 * @param {Array<object>} tasks
 * @param {(task: object) => Array<string>} [resourceIdsFor] - Op welke rij een taak hoort in de roosterweergave
 * @param {Record<string, Date>} [scheduleEndByPersonaId] - Laatste roosterdag per uitgestroomde deelnemer
 * @param {import('../../../helpers/calendar/scheduleDisplayFor').ScheduleDisplay} [scheduleDisplay] - Rooster als blok, achtergrond of verborgen
 */
const tasksToEvents = (tasks, resourceIdsFor, scheduleEndByPersonaId = {}, scheduleDisplay = 'block') =>
  tasks.flatMap((task) => {
    // Na de uitstroomdatum is er geen roosterblok meer: een reeks krijgt een
    // einddatum, een los blok daarna valt weg — net als in de agenda.
    const { lastDay, past } = scheduleCutoff(task, scheduleEndByPersonaId);
    if (past) return [];

    const isSchedule = task.Type === 'personaSchedule';
    if (isSchedule && scheduleDisplay === 'hidden') return [];

    // Dezelfde kleurregels als de echte agenda. Zonder dit staan hier roze
    // roosterblokken terwijl de agenda één scherm verder grijs toont.
    // Persoonskleuren doen hier niet mee: de plankiezer kent ze niet.
    // AI-CONTEXT: See docs/pitfalls.md#dev-second-source-never-reaches-its-own-gate
    const style = agendaEventStyle(task, {}, getTaskTypeStyle(task.Type));
    const event = {
      id: task.docid,
      title: task.Title,
      start: task.Timestamp?.toDate(),
      end: task.TimestampEnd?.toDate(),
      allDay: task.TimestampEnd === undefined || task.TimestampEnd === null,
      color: style.color,
      borderColor: style.borderColor,
      backgroundColor: style.backgroundColor,
      textColor: style.textColor,
      editable: false,
      extendedProps: { ID: task.ID, Type: task.Type, PersonaID: task.PersonaID || null },
    };

    // Zonder een bestaande rij tekent FullCalendar het blok in de
    // roosterweergave niet, zonder fout en zonder waarschuwing — dan zou je
    // inplannen op een uur dat er bezet uitziet omdat er niets staat.
    if (resourceIdsFor) event.resourceIds = resourceIdsFor(task);

    const recurring = buildRecurringEventProps(task, lastDay);
    if (recurring) {
      event.rrule = recurring.rrule;
      event.exdate = recurring.exdate;
      event.exrule = recurring.exrule;
      event.duration = recurring.duration;
    }

    // Dezelfde regel als de agenda: een vlak krijgt zijn kleur uit calendar.css.
    return [isSchedule && scheduleDisplay === 'background' ? asScheduleBackground(event) : event];
  });

const INITIAL_VIEW = 'timeGridWeek';
const NO_PERSONA_IDS = [];

/**
 * FullCalendar showing all organisation tasks, slot selection only — no modal.
 *
 * @param {object} props
 * @param {function(Date, Date, string=): void} props.onSelect - Aangeroepen met
 *   (start, eind) en, als er op de regel van een medewerker geklikt is, diens id
 * @param {boolean} [props.fixtool] - Of de organisatie FIXtool heeft; zonder blijft het rooster een blok
 * @param {*} [props.scheduleSetting] - `CompagnyMeta.AgendaScheduleDisplay`, ruw uit de useSettings van de ouder
 */
const CalendarSlotPicker = ({ onSelect, fixtool, scheduleSetting }) => {
  const taskDTO = useTaskDTO();
  const [docs, setDocs] = useState([]);
  const [dateRange, setDateRange] = useState(null);

  // Dezelfde lijst als de agenda gebruikt voor de rijen van het rooster: álle
  // medewerkers, want wie niet meer actief is maar nog afspraken heeft staan
  // hoort een eigen regel te houden.
  const personas = useFirestore('Personas');
  // Wiens rooster de tijdkiezer laat zien: dezelfde regel als de agenda. Tot
  // 2026-10-16 stond hier het rooster van iedereen, dus leek een uur bezet door
  // iemand die al lang weg was.
  const { visibleDocs, schedulePersonaIds, scheduleEndByPersonaId } = useAgendaSchedule(personas, docs, dateRange?.start);
  // Afwezigheid voor dezelfde groep als het rooster, zoals in de agenda. Zonder
  // dit leek een afwezige medewerker hier beschikbaar.
  const personaAbsences = useFirestorePersonaAbsences(schedulePersonaIds, client);

  useEffect(() => {
    if (!dateRange) return;

    const unsubscribe = taskDTO.queryBy({
      query: (q) => q,
      dateStart: dateRange.start,
      dateEnd: dateRange.end,
      onUpdate: setDocs,
    });

    return unsubscribe;
  }, [dateRange]);

  return (
    <CalendarFilters docs={visibleDocs} initialSelectedTypes={['addressVisit']}>
      {({ filteredDocs, selectedPersonaIds }) => (
        <SlotCalendar
          filteredDocs={filteredDocs}
          selectedPersonaIds={selectedPersonaIds}
          fixtool={fixtool}
          scheduleSetting={scheduleSetting}
          personaAbsences={Array.isArray(personaAbsences) ? personaAbsences : []}
          scheduleEndByPersonaId={scheduleEndByPersonaId}
          personas={Array.isArray(personas) ? personas : []}
          onSelect={onSelect}
          onDatesSet={setDateRange}
        />
      )}
    </CalendarFilters>
  );
};

/**
 * @param {object} props
 * @param {Array<object>} props.filteredDocs - De taken na het typefilter
 * @param {Array<string>} [props.selectedPersonaIds] - Het medewerkerfilter (leeg = iedereen)
 * @param {boolean} [props.fixtool] - Of de organisatie FIXtool heeft
 * @param {*} [props.scheduleSetting] - `CompagnyMeta.AgendaScheduleDisplay`, ruw
 * @param {Array<object>} props.personaAbsences - PersonaAbsences van de medewerkers met een rooster in beeld
 * @param {Record<string, Date>} props.scheduleEndByPersonaId - Laatste roosterdag per uitgestroomde deelnemer
 * @param {Array<object>} props.personas - Alle deelnemers, voor de rijen van het rooster
 * @param {function(Date, Date, string=): void} props.onSelect - Gekozen tijdslot
 * @param {(range: {start: Date, end: Date}) => void} props.onDatesSet - Het zichtbare bereik
 */
const SlotCalendar = ({ filteredDocs, selectedPersonaIds = NO_PERSONA_IDS, fixtool, scheduleSetting, personaAbsences, scheduleEndByPersonaId, personas, onSelect, onDatesSet }) => {
  const [activeView, setActiveView] = useState(null);
  // Geen schakelaar naar huishoudens hier: je plant een bezoek IN voor één
  // huishouden, dus een tijdslot kiezen op de regel van een ánder huishouden
  // zegt niets. De medewerkers zijn juist wél de vraag: wie kan wanneer.
  const timeline = useTimelineView({ personas, tasks: filteredDocs, allowModeSwitch: false });

  // Rooster als blok, achtergrond of verborgen: dezelfde instelling en regels als
  // de agenda, anders toont dit venster blokken waar de agenda vlakken toont.
  // AI-CONTEXT: See docs/features/afspraak-soort-en-aanwezigheid-bkb.md
  const viewType = activeView || INITIAL_VIEW;
  const scheduleDisplay = scheduleDisplayFor({
    fixtool,
    setting: resolveAgendaOptions({ AgendaScheduleDisplay: scheduleSetting }).scheduleDisplay,
    viewType,
    timelineMode: timeline.mode,
    selectedPersonaIds,
  });
  const idsWithSchedule = useMemo(
    () => personaIdsWithSchedule(filteredDocs, scheduleEndByPersonaId),
    [filteredDocs, scheduleEndByPersonaId]
  );
  const scheduleOff = scheduleOffClasses({ display: scheduleDisplay, viewType, idsWithSchedule, selectedPersonaIds });

  const events = useMemo(
    () => tasksToEvents(filteredDocs, timeline.enabled ? timeline.resourceIdsFor : undefined, scheduleEndByPersonaId, scheduleDisplay),
    [filteredDocs, timeline, scheduleEndByPersonaId, scheduleDisplay]
  );

  return (
    <FullCalendar
      plugins={[rrulePlugin, dayGridPlugin, timeGridPlugin, interactionPlugin, ...timeline.plugins]}
      {...timeline.options}
      views={timeline.views}
      initialView={INITIAL_VIEW}
      locale={nlLocale}
      selectable={true}
      selectMirror={true}
      nowIndicator={true}
      editable={false}
      events={events}
      resourceLaneClassNames={arg => scheduleOff.laneClassNames(arg.resource?.id)}
      viewClassNames={() => scheduleOff.viewClassNames}
      // Per getekende occurrence, zoals in de agenda: een reeks is één event,
      // en alleen de afwezige dagen horen gemarkeerd.
      eventClassNames={arg => personaAbsenceClassNames(
        { type: arg.event.extendedProps.Type, personaId: arg.event.extendedProps.PersonaID, start: arg.event.start, end: arg.event.end },
        personaAbsences
      )}
      // Kiest iemand een slot op de regel van een medewerker, dan hoort die
      // medewerker op de afspraak te komen. Anders wijs je Kim aan en staat er
      // daarna niemand bij, terwijl het scherm het tegendeel liet zien.
      select={e => onSelect(
        e.start,
        e.end,
        timeline.isPersonaRow(e.resource?.id) ? e.resource.id : undefined
      )}
      datesSet={dateInfo => {
        setActiveView(dateInfo.view?.type || null);
        onDatesSet({ start: dateInfo.start, end: dateInfo.end });
      }}
      headerToolbar={{
        left: 'prev,next today',
        center: 'title',
        right: `timeGridWeek,timeGridDay${timeline.toolbarButton}`,
      }}
      // Zonder vaste hoogte rendert FullCalendar alle 24 uur uit en start de
      // weergave dus op 00:00. Met een hoogte krijgt de kalender een eigen
      // scroller, waardoor scrollTime de werkdag in beeld zet.
      //
      // Behalve in de roosterweergave: die heeft één regel per medewerker en is
      // dus meestal een paar honderd pixels hoog. 620 vast zou het venster laten
      // scrollen om lege ruimte te tonen, terwijl de onderste medewerkers juist
      // buiten beeld vielen.
      height={activeView === TIMELINE_VIEW ? 'auto' : 620}
      scrollTime="08:00:00"
    />
  );
};

export default CalendarSlotPicker;
